import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { formatMonthKey, calculateAge } from '@/utils/dateUtils';
import { Baby } from 'lucide-react';

export default function Timeline() {
  const navigate = useNavigate();
  const { memories, children } = useStore();

  // 按月份分组
  const groupedMemories = useMemo(() => {
    const groups: Record<string, typeof memories> = {};
    memories.forEach(memory => {
      const monthKey = formatMonthKey(memory.createdAt);
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(memory);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, items]) => ({ month, memories: items }));
  }, [memories]);

  if (memories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-400">
        <Baby className="w-16 h-16 mb-4 opacity-50" />
        <p>暂无记录</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="relative">
        {/* 时间轴线 */}
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-300 via-primary-400 to-primary-300" />
        
        <div className="space-y-8">
          {groupedMemories.map(({ month, memories: monthMemories }) => (
            <div key={month} className="relative">
              {/* 月份标记 */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-6 rounded-full bg-primary-500 border-2 border-white shadow-md flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-white" />
                </div>
                <span className="text-lg font-bold text-gray-800">
                  {formatMonthKey(month)}
                </span>
                <span className="text-sm text-gray-500">
                  {monthMemories.length} 条记录
                </span>
              </div>

              {/* 该月的记录 */}
              <div className="ml-9 space-y-3">
                {monthMemories.map((memory, idx) => {
                  const child = children.find(c => c.id === memory.childId);
                  const isLast = idx === monthMemories.length - 1;
                  
                  return (
                    <div
                      key={memory.id}
                      onClick={() => navigate(`/memory/${memory.id}`)}
                      className="bg-white rounded-xl p-4 card-shadow cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex gap-3">
                        {/* 左侧时间线 */}
                        <div className="flex flex-col items-center">
                          <div 
                            className="w-3 h-3 rounded-full border-2 border-white shadow"
                            style={{ backgroundColor: child?.color || '#ccc' }}
                          />
                          {!isLast && (
                            <div className="w-0.5 flex-1 bg-gray-200 mt-1" />
                          )}
                        </div>

                        {/* 内容 */}
                        <div className="flex-1 pb-2">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium" style={{ color: child?.color }}>
                              {child?.name}
                            </span>
                            {child?.birthDate && child.type !== 'family' && (
                              <span className="text-xs text-gray-400">
                                {calculateAge(child.birthDate, memory.createdAt)}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 ml-auto">
                              {new Date(memory.createdAt).toLocaleDateString('zh-CN', {
                                day: 'numeric'
                              })}日
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                            {memory.content}
                          </p>

                          {memory.media.length > 0 && (
                            <div className="flex gap-2">
                              {memory.media.slice(0, 3).map(media => (
                                <div 
                                  key={media.id}
                                  className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100"
                                >
                                  {media.type === 'image' ? (
                                    <img 
                                      src={media.url} 
                                      alt="" 
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <video 
                                      src={media.url}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              ))}
                              {memory.media.length > 3 && (
                                <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                                  +{memory.media.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="h-20" />
    </div>
  );
}
